import { List, Stack, Table, Text, Title } from '@mantine/core';
import type { ReactNode } from 'react';
import { IME_APLIKACIJE } from '../ime';
import { RAZLICICA_POGOJEV } from './razlicica';

const PONUDNIK =
  'LunaLabs, programske rešitve d.o.o., Vodovodna ulica 30C, 2000 Maribor, matična številka 8857482000, ID za DDV SI94972737';

function Clen({ st, naslov, children }: { st: number; naslov: string; children: ReactNode }) {
  return (
    <Stack gap={6} component="section" aria-labelledby={`clen-${st}`}>
      <Title order={3} size="h5" id={`clen-${st}`} mt="sm">
        {st}. {naslov}
      </Title>
      {children}
    </Stack>
  );
}

function T({ children }: { children: ReactNode }) {
  return (
    <Text size="sm" style={{ lineHeight: 1.6 }}>
      {children}
    </Text>
  );
}

function Seznam({ children }: { children: ReactNode }) {
  return (
    <List size="sm" spacing={4} withPadding style={{ lineHeight: 1.6 }}>
      {children}
    </List>
  );
}

/**
 * Splošni pogoji uporabe aplikacije Avto Pisarna (z določili o varstvu osebnih podatkov in obdelavi po 28. členu
 * GDPR). Prikazani v aplikaciji: javna stran {@code /splosni-pogoji}, sprejem ob prvi prijavi, povezava pri
 * registraciji. Številke (preizkus, roki zaklepa) so iste kot nastavitve strežnika {@code hera.narocnina.*}.
 * Pogoji so namerno splošni: aplikacijo opišejo po modulih in skupinah funkcij, posamezne funkcije pa so v navodilih
 * ({@code src/pomoc/clanki}). Nova funkcija obstoječega modula ali nova funkcija z umetno inteligenco iz skupin v 10.
 * členu ne zahteva spremembe (19. člen); spremeniti jih je treba ob novem modulu, novi vrsti osebnih podatkov ali
 * podobdelovalcu, spremembi plačil ali odgovornosti.
 */
export function BesediloPogojev() {
  return (
    <Stack gap="xs">
      <Text size="xs" c="dimmed">
        Različica z dne {RAZLICICA_POGOJEV}
      </Text>
      <T>
        Ti splošni pogoji urejajo razmerje med ponudnikom aplikacije {IME_APLIKACIJE} in podjetji, ki aplikacijo
        uporabljajo, ter njihovimi uporabniki. Ponudnik je {PONUDNIK} (v nadaljevanju: <b>ponudnik</b> ali <b>mi</b>).
      </T>

      <Clen st={1} naslov="Pojmi">
        <Seznam>
          <List.Item>
            <b>{IME_APLIKACIJE}</b> je spletna aplikacija na naslovu app.avtopisarna.si za poslovanje podjetij v
            avtomobilski dejavnosti. Sestavljena je iz <b>modulov</b>: modul <b>Prodaja vozil</b> je namenjen odkupu,
            prodaji in posredovanju vozil, modul <b>Delavnica</b> vzdrževanju in popravilom vozil strank. Obema sta
            skupni stranke, predračuni, računi z davčnim potrjevanjem, dokumenti, nastavitve in funkcije z umetno
            inteligenco.
          </List.Item>
          <List.Item>
            <b>Paket</b> določa, katere module ima naročnik (Prodaja vozil, Delavnica ali oboje). Funkcije modulov so
            opisane v aplikaciji in v navodilih (app.avtopisarna.si/navodila); aplikacijo sproti dopolnjujemo z novimi
            funkcijami in jih lahko spreminjamo, pri čemer bistvene namembnosti modula ne zmanjšamo.
          </List.Item>
          <List.Item>
            <b>Naročnik</b> je podjetje (pravna oseba ali samostojni podjetnik), ki je registriralo uporabo aplikacije.
          </List.Item>
          <List.Item>
            <b>Uporabnik</b> je fizična oseba, ki se v aplikacijo prijavi z uporabniškim imenom in geslom v imenu
            naročnika.
          </List.Item>
          <List.Item>
            <b>Skrbnik naročnika</b> je uporabnik, ki je naročnika registriral, ali uporabnik, ki ga naročnik za to
            določi; v imenu naročnika ureja uporabnike, nastavitve in naročnino.
          </List.Item>
          <List.Item>
            <b>Naročnina</b> je mesečno plačilo za uporabo aplikacije; plačuje se vnaprej s <b>točkami</b> (1 € = 1
            točka).
          </List.Item>
          <List.Item>
            <b>Funkcije z umetno inteligenco</b> so funkcije aplikacije, pri katerih podatke obdela jezikovni model
            zunanjega ponudnika (npr. branje dokumentov, pisanje besedil, urejanje fotografij); plačujejo se s točkami
            po porabi.
          </List.Item>
          <List.Item>
            <b>Stranke naročnika</b> so osebe, s katerimi naročnik posluje (kupci, prodajalci, lastniki vozil v
            popravilu …) in katerih podatke vnese v aplikacijo.
          </List.Item>
        </Seznam>
      </Clen>

      <Clen st={2} naslov="Komu je aplikacija namenjena">
        <T>
          {IME_APLIKACIJE} je namenjena izključno podjetjem s sedežem v Sloveniji, ki so vpisana v register davčnih
          zavezancev, za njihovo poslovno dejavnost. Ni namenjena potrošnikom, zato za razmerje med ponudnikom in
          naročnikom ne veljajo predpisi o varstvu potrošnikov.
        </T>
      </Clen>

      <Clen st={3} naslov="Registracija in sklenitev pogodbe">
        <Seznam>
          <List.Item>
            Ob registraciji naročnik vpiše davčno številko, podatke podjetja (preverimo jih v javnem poslovnem registru)
            in podatke prvega uporabnika. Uporabniško ime je e-poštni naslov, ki je tudi kontakt podjetja.
          </List.Item>
          <List.Item>
            Pogodba o uporabi aplikacije je sklenjena, ko oseba, ki registrira podjetje, sprejme te pogoje in potrdi
            registracijo z nastavitvijo gesla. S tem izjavlja, da je za to pooblaščena in da so vpisani podatki
            resnični. Pogodba je sklenjena za nedoločen čas.
          </List.Item>
          <List.Item>Ob registraciji naročnik izbere paket; kasneje ga lahko spremeni po 5. členu.</List.Item>
          <List.Item>Za vsako podjetje je mogoča ena registracija (ena davčna številka).</List.Item>
        </Seznam>
      </Clen>

      <Clen st={4} naslov="Brezplačni preizkus">
        <T>
          Nova registracija vključuje <b>3 dni brezplačnega preizkusa</b> z vsemi funkcijami izbranega paketa. Preizkus
          ni vezan na nobeno obveznost in se po izteku ne zaračuna samodejno. Če naročnik po preizkusu naročnine ne
          plača, se dostop zaklene po pravilih iz 6. člena; vneseni podatki do izbrisa ostanejo shranjeni.
        </T>
      </Clen>

      <Clen st={5} naslov="Naročnina in plačila">
        <Seznam>
          <List.Item>
            Višina mesečne naročnine (z DDV) je odvisna od paketa in je navedena v ceniku ob registraciji in v
            aplikaciji na zaslonu Naročnina. Za posameznega naročnika se lahko pisno dogovorimo drugače (posebna cena).
          </List.Item>
          <List.Item>
            Paket lahko skrbnik naročnika razširi na zaslonu Naročnina; dodani moduli so na voljo takoj. Ob razširitvi
            se od točk takoj odšteje sorazmerni del razlike v ceni za preostanek že plačanega obdobja (med preizkusom
            doplačila ni), od naslednjega obračunskega dne pa velja cena novega paketa. Zmanjšanje paketa uredimo na
            zahtevo naročnika (povratne informacije) z učinkom od naslednjega obračunskega dne. Ko naročnik sam spremeni
            paket, dogovorjena posebna cena preneha in velja cenik.
          </List.Item>
          <List.Item>
            Naročnina se plačuje vnaprej z nakazilom: na naš transakcijski račun s sklicem naročnika (UPN QR) ali na
            račun pri ponudniku plačilnih storitev Stripe, prikazan v aplikaciji. Vsak plačani evro je ena točka; pri
            večjih nakazilih lahko prejmete bonus točke po lestvici, objavljeni v aplikaciji. Pri nakazilu prek Stripa
            se odšteje strošek obdelave, prikazan v aplikaciji.
          </List.Item>
          <List.Item>
            Na obračunski dan (dan v mesecu, ko se je končal preizkus) se od točk odšteje mesečna naročnina in plačano
            obdobje se podaljša za en mesec. Za vsako nakazilo izdamo račun.
          </List.Item>
          <List.Item>
            S točkami se poleg naročnine plačuje tudi uporaba funkcij z umetno inteligenco po 10. členu; ta poraba se
            sproti odšteje od točk in je vidna v zgodovini točk.
          </List.Item>
          <List.Item>
            Plačanih točk ne vračamo, razen če pogodba preneha iz razlogov na naši strani; takrat vrnemo vrednost
            neporabljenih plačanih točk. Bonus točke niso izplačljive.
          </List.Item>
          <List.Item>
            Spremembo cenika objavimo v aplikaciji najmanj 30 dni pred začetkom veljave; že plačane točke ostanejo
            veljavne.
          </List.Item>
        </Seznam>
      </Clen>

      <Clen st={6} naslov="Zamuda, zaklep in izbris podatkov">
        <T>Za plačano obdobje velja datum »plačano do« (D), ki je viden v aplikaciji:</T>
        <Table.ScrollContainer minWidth={300}>
          <Table withTableBorder withColumnBorders fz="sm" verticalSpacing={6}>
            <Table.Thead>
              <Table.Tr>
                <Table.Th>Obdobje</Table.Th>
                <Table.Th>Kaj to pomeni</Table.Th>
              </Table.Tr>
            </Table.Thead>
            <Table.Tbody>
              <Table.Tr>
                <Table.Td>14 dni pred D</Table.Td>
                <Table.Td>opozorilo v aplikaciji, če točk ni dovolj za naslednji mesec</Table.Td>
              </Table.Tr>
              <Table.Tr>
                <Table.Td>D do D + 10 dni</Table.Td>
                <Table.Td>zamuda: aplikacija deluje normalno, z opozorilom</Table.Td>
              </Table.Tr>
              <Table.Tr>
                <Table.Td>D + 10 do D + 90 dni</Table.Td>
                <Table.Td>
                  zaklep: na voljo je samo zaslon za plačilo; ob ponovnem vklopu se zaračuna še 10 točk in čas zamude,
                  ne pa čas zaklepa
                </Table.Td>
              </Table.Tr>
              <Table.Tr>
                <Table.Td>D + 90 do D + 180 dni</Table.Td>
                <Table.Td>dokončen zaklep: prijava ni mogoča; podatke lahko na zahtevo izvozimo</Table.Td>
              </Table.Tr>
              <Table.Tr>
                <Table.Td>po D + 180 dni</Table.Td>
                <Table.Td>podatki naročnika se nepovratno izbrišejo</Table.Td>
              </Table.Tr>
            </Table.Tbody>
          </Table>
        </Table.ScrollContainer>
        <T>
          Naročnik mora izdane račune in drugo dokumentacijo hraniti toliko časa, kot zahtevajo predpisi (za račune 10
          let po ZDDV-1). Pred izbrisom ga na to opozorimo; podatke lahko prej izvozi z možnostmi aplikacije ali nas
          zaprosi za izvoz.
        </T>
      </Clen>

      <Clen st={7} naslov="Uporabniki in dostop">
        <Seznam>
          <List.Item>
            Skrbnik naročnika lahko dodaja uporabnike in jim ponastavi geslo. Naročnik odgovarja za dejanja svojih
            uporabnikov v aplikaciji.
          </List.Item>
          <List.Item>
            Uporabnik mora geslo varovati in ga ne sme deliti z drugimi. Ob sumu zlorabe naj geslo takoj zamenja in nas
            obvesti.
          </List.Item>
          <List.Item>
            Z istim uporabniškim imenom ima lahko uporabnik dostop do več podjetij, če mu ga ta omogočijo.
          </List.Item>
        </Seznam>
      </Clen>

      <Clen st={8} naslov="Dovoljena uporaba">
        <T>
          Aplikacija se sme uporabljati samo za zakonito poslovno dejavnost naročnika, kateri so namenjeni moduli
          njegovega paketa (1. člen), in s tem povezane storitve. Prepovedano je zlasti:
        </T>
        <Seznam>
          <List.Item>
            ovirati delovanje aplikacije ali drugih uporabnikov, poskušati vdreti ali obiti zaščito,
          </List.Item>
          <List.Item>vnašati nezakonite vsebine ali datoteke s škodljivo kodo,</List.Item>
          <List.Item>samodejno zajemati podatke ali aplikacijo kopirati, posnemati ali preprodajati,</List.Item>
          <List.Item>uporabljati lažne podatke ali tuje identitete,</List.Item>
          <List.Item>pošiljati strankam ali drugim osebam nezaželena oglasna sporočila prek aplikacije.</List.Item>
        </Seznam>
      </Clen>

      <Clen st={9} naslov="Davčno potrjevanje, dokumenti in sporočila strankam">
        <Seznam>
          <List.Item>
            Aplikacija tehnično omogoča davčno potrjevanje računov pri FURS. Za pravilnost podatkov (poslovni prostori,
            naprave, DDV, zneski), veljavnost digitalnega potrdila in za izpolnjevanje davčnih obveznosti odgovarja
            naročnik.
          </List.Item>
          <List.Item>
            Predloge pogodb in drugih dokumentov so vzorci, ki jih naročnik prilagodi svojemu poslovanju. Za njihovo
            vsebino in pravno ustreznost odgovarja naročnik.
          </List.Item>
          <List.Item>
            Sporočila strankam naročnika (npr. potrditev termina, obvestilo, da je vozilo pripravljeno, povezava za
            odobritev dodatnih del, račun po e-pošti) aplikacija pošlje v imenu naročnika, ko jih uporabnik pošlje ali
            vklopi. Besedila so privzeta in jih naročnik lahko prilagodi; za vsebino in za to, da sme stranki pisati,
            odgovarja naročnik. Vsako poslano sporočilo zabeležimo (prejemnik, vsebina, uspeh).
          </List.Item>
          <List.Item>
            Javne strani, ki jih aplikacija ustvari za stranke naročnika (npr. ponudba vozil, stran za odobritev
            dodatnih del), prikazujejo podatke, ki jih naročnik objavi ali pošlje; za njihovo pravilnost odgovarja
            naročnik. Odgovor stranke na taki strani (npr. odobritev) zabeležimo s časom in IP-naslovom.
          </List.Item>
          <List.Item>
            Podpis stranke na zaslonu (npr. ob sprejemu vozila) je shranjena slika lastnoročnega podpisa in ni
            kvalificirani elektronski podpis; ali zadošča za namen naročnika, presodi naročnik.
          </List.Item>
          <List.Item>
            Aplikacijo prilagajamo veljavnim slovenskim predpisom za primere uporabe, ki jih podpira. Zakonskih evidenc,
            ki jih aplikacija ne vodi izrecno (npr. evidence odpadkov), aplikacija ne nadomešča.
          </List.Item>
        </Seznam>
      </Clen>

      <Clen st={10} naslov="Funkcije z umetno inteligenco">
        <Seznam>
          <List.Item>
            Aplikacija ponuja funkcije z umetno inteligenco v teh skupinah: <b>branje podatkov</b> iz fotografij ali PDF
            dokumentov (npr. prometno dovoljenje, potrdilo o skladnosti, pogodba, račun, osebni dokument prodajalca),
            <b>pisanje besedil</b> iz podatkov in zapiskov uporabnika (npr. opis vozila), <b>urejanje fotografij</b>
            (npr. zamenjava registrske tablice z logotipom naročnika, zamenjava ozadja, odstranitev motečih predmetov)
            in
            <b>asistent</b>, ki mu uporabnik zastavlja vprašanja v naravnem jeziku. Posamezne funkcije in kje so v
            aplikaciji, so opisane v navodilih (Umetna inteligenca); nabor se lahko dopolnjuje v okviru teh skupin.
          </List.Item>
          <List.Item>
            Asistent iz vprašanja izbere le, kaj naj aplikacija pokaže; številke in podatke izračuna in prikaže
            aplikacija iz podatkov naročnika, jezikovni model jih ne prejme. Na izrecno zahtevo uporabnika
            (»Komentiraj«) asistent napiše komentar k prikazanim številkam. Vprašanja, ki jih asistent še ne zna,
            posreduje ponudniku kot povratno informacijo naročnika, da asistenta dopolni. Spremembe podatkov s pomočjo
            umetne inteligence se izvedejo šele, ko jih uporabnik potrdi.
          </List.Item>
          <List.Item>
            Funkcije so naročniku na voljo samodejno; skrbnik naročnika jih lahko kadar koli izklopi v Nastavitvah
            (Umetna inteligenca). Ponudnik jih lahko posameznemu naročniku omeji ali onemogoči.
          </List.Item>
          <List.Item>
            <b>Cena:</b> vsaka uporaba se ob izvedbi odšteje od točk glede na dejansko porabo (DDV je vključen); znesek
            posamezne uporabe je viden v zgodovini točk. Uporaba je mogoča, dokler ima naročnik pozitivno stanje točk;
            če uporaba stane več od stanja, se odšteje samo stanje (točke ne gredo v minus). Mesečna poraba je lahko
            omejena (omejitev je vidna v aplikaciji). Ponudnik lahko naročniku za preizkus funkcij podari točke.
            Porabljenih točk ne vračamo, tudi če rezultat ni uporaben (npr. urejena fotografija, ki je uporabnik ne
            sprejme).
          </List.Item>
          <List.Item>
            <b>Obdelava:</b> podatke, ki jih uporabnik da v obdelavo za posamezno funkcijo (npr. dokumenti, fotografije,
            podatki vozila in zapiski, pri urejanju fotografij tudi logotip in fotografije ozadja naročnika, pri
            asistentu besedilo vprašanj, pri komentarju asistenta pa zbirne številke s kartice brez imen oseb),
            aplikacija pošlje ponudniku jezikovnega modela (Google, storitev Gemini API v plačljivi različici) samo za
            izvedbo zahteve. Ponudnik modela teh podatkov ne uporablja za izboljševanje svojih storitev in jih lahko
            hrani le omejen čas za odkrivanje zlorab. Prebrani dokumenti se lahko shranijo k zapisu, pri katerem so bili
            prebrani (npr. vozilu), kot podatki naročnika (niso javni); urejene fotografije se pri sliki hranijo 7 dni
            za primerjavo, sprejeta urejena fotografija pa dokončno zamenja izvirnik. Za vsako uporabo beležimo čas,
            uporabnika, funkcijo in porabo, za pomoč naročniku, odpravljanje napak in preprečevanje zlorab pa 90 dni
            hranimo tudi besedilo zahteve in odgovora modela (brez fotografij in dokumentov), nato ga izbrišemo; dostop
            do njega ima samo ponudnik aplikacije. Fotografije, ki jih ustvari ali uredi model, lahko vsebujejo neviden
            digitalni vodni žig ponudnika modela (Google SynthID).
          </List.Item>
          <List.Item>
            <b>Rezultati so predlogi.</b> Umetna inteligenca se lahko zmoti (napačno prebran podatek, netočen ali
            nepopoln opis). Aplikacija izpolni samo prazna polja in pokaže razlike, uporabnik pa mora rezultat pred
            shranjevanjem oziroma objavo preveriti. Urejena fotografija mora prikazovati vozilo, kakršno je: pred
            sprejemom in objavo naročnik preveri, da umetna inteligenca ni spremenila vozila (npr. poškodb, opreme,
            barve, platišč). Za pravilnost shranjenih in objavljenih podatkov in fotografij (npr. opisa in slik vozila v
            javni ponudbi) odgovarja naročnik.
          </List.Item>
          <List.Item>
            Uporabnik sme v obdelavo dati le dokumente in podatke, ki jih naročnik zakonito obdeluje za svoje poslovanje
            (npr. dokumente vozila, ki ga odkupuje ali popravlja, in osebni dokument prodajalca za sklenitev pogodbe).
          </List.Item>
        </Seznam>
      </Clen>

      <Clen st={11} naslov="Delovanje in vzdrževanje">
        <T>
          Trudimo se za nemoteno delovanje aplikacije. Načrtovana vzdrževalna dela izvajamo praviloma izven delovnega
          časa in jih vnaprej napovemo v aplikaciji. Za motnje zaradi interneta, opreme naročnika, ponudnikov tretjih
          storitev (npr. FURS) ali višje sile ne odgovarjamo.
        </T>
      </Clen>

      <Clen st={12} naslov="Pomoč">
        <T>
          Za vprašanja, predloge, prijavo napak in reklamacije nam pišite prek gumba »Povratne informacije« v
          aplikaciji. Odgovarjamo ob delavnikih med 8. in 16. uro, praviloma isti ali naslednji delovni dan. Napake, ki
          onemogočajo delo, odpravimo prednostno. Reklamacijo računa je treba sporočiti v 8 dneh od prejema.
        </T>
      </Clen>

      <Clen st={13} naslov="Podatki naročnika">
        <T>
          Podatki, ki jih naročnik vnese v aplikacijo (vozila, stranke, delovni nalogi, dokumenti, fotografije …), so
          last naročnika. Uporabljamo jih samo za delovanje aplikacije in pomoč naročniku, ne prodajamo jih in jih ne
          posredujemo tretjim osebam, razen podobdelovalcem iz 15. člena ali na podlagi zakona oz. odločbe pristojnega
          organa. Za izboljševanje aplikacije lahko uporabimo zbirne, anonimizirane podatke o uporabi.
        </T>
      </Clen>

      <Clen st={14} naslov="Osebni podatki, za katere smo upravljavec">
        <T>
          Za vodenje uporabniških računov, naročnin in komunikacijo obdelujemo: ime, priimek, e-poštni naslov in telefon
          uporabnikov, podatke podjetja, podatke o plačilih, podatke o prijavah in uporabi (čas, naprava, IP-naslov),
          evidenco poslanih e-poštnih sporočil, evidenco uporabe funkcij z umetno inteligenco (čas, uporabnik, funkcija,
          poraba; besedilo zahtev in odgovorov 90 dni, kot določa 10. člen) ter podatek, kje ste izvedeli za nas. Pravna
          podlaga je izvajanje pogodbe, zakonske obveznosti (npr. računovodstvo) in naš zakoniti interes (varnost,
          preprečevanje zlorab, izboljševanje storitve).
        </T>
        <T>
          Podatke hranimo, dokler traja pogodba, in nato toliko časa, kot je potrebno za izpolnitev zakonskih obveznosti
          ali uveljavljanje pravnih zahtevkov; računovodske listine skladno s predpisi. Posameznik ima pravico do
          dostopa, popravka, izbrisa, omejitve obdelave, prenosljivosti in ugovora ter pravico do pritožbe pri
          Informacijskem pooblaščencu (www.ip-rs.si). Zahtevo lahko pošlje prek aplikacije ali pisno na naslov
          ponudnika.
        </T>
      </Clen>

      <Clen st={15} naslov="Obdelava osebnih podatkov v imenu naročnika">
        <T>
          Za osebne podatke, ki jih naročnik vnese v aplikacijo, je upravljavec naročnik, ponudnik pa je njegov
          obdelovalec. Ta člen je pogodba o obdelavi osebnih podatkov po 28. členu Splošne uredbe o varstvu podatkov
          (GDPR):
        </T>
        <Seznam>
          <List.Item>
            <b>Predmet in trajanje:</b> zagotavljanje aplikacije {IME_APLIKACIJE} za čas trajanja pogodbe in do izbrisa
            po 6. členu.
          </List.Item>
          <List.Item>
            <b>Vrste podatkov:</b> podatki strank naročnika (ime, priimek, naziv, naslov, davčna in matična številka,
            EMŠO, številka osebnega dokumenta, transakcijski račun, kontaktni podatki), podatki o njihovih vozilih in
            poslih (npr. registrska številka, številka šasije, kilometri, opravljena dela, termini, računi), dokumenti,
            fotografije in podpisi, ki jih naročnik naloži ali zajame (npr. prometno dovoljenje, osebni dokument
            prodajalca, fotografije vozila ob sprejemu), odgovori strank na javnih straneh (čas, IP-naslov), podatki
            uporabnikov naročnika ter drugi podatki, ki jih naročnik vnese.
          </List.Item>
          <List.Item>
            <b>Namen in narava obdelave:</b> hramba, prikaz, izdelava dokumentov, davčno potrjevanje računov, pošiljanje
            sporočil strankam v imenu naročnika in javne strani zanje (9. člen), funkcije z umetno inteligenco (ko jih
            naročnik uporablja, 10. člen), varnostne kopije in tehnična pomoč – samo po navodilih naročnika, ki jih daje
            z uporabo aplikacije.
          </List.Item>
          <List.Item>
            <b>Zaupnost:</b> do podatkov imajo dostop samo pooblaščene osebe ponudnika, ki so zavezane k molčečnosti, in
            le, kolikor je potrebno za delovanje in pomoč.
          </List.Item>
          <List.Item>
            <b>Varnost:</b> šifrirane povezave (HTTPS), zgoščena gesla, šifrirana hramba digitalnih potrdil, ločeni
            podatki vsakega naročnika, varnostne kopije najmanj enkrat dnevno, omejen in beležen dostop.
          </List.Item>
          <List.Item>
            <b>Podobdelovalci:</b> Hetzner Online GmbH, Nemčija (gostovanje strežnikov in varnostnih kopij v EU), Google
            Ireland Ltd. (pošiljanje e-pošte iz aplikacije in obdelava z umetno inteligenco – Gemini API) in Stripe
            Payments Europe Ltd. (plačila naročnine). Naročnik se strinja z njihovo uporabo; o novem podobdelovalcu ga
            obvestimo vnaprej in mu omogočimo ugovor. Če ti podatke prenašajo izven EU, je to urejeno s standardnimi
            pogodbenimi klavzulami ali drugim ustreznim zaščitnim ukrepom.
          </List.Item>
          <List.Item>
            <b>Pomoč upravljavcu:</b> naročniku pomagamo pri odgovorih na zahteve posameznikov in pri zagotavljanju
            varnosti; o kršitvi varnosti osebnih podatkov ga obvestimo brez nepotrebnega odlašanja, najkasneje v 48 urah
            od seznanitve.
          </List.Item>
          <List.Item>
            <b>Ob prenehanju:</b> podatke izbrišemo po 6. členu, prej jih na zahtevo naročniku vrnemo (izvoz).
          </List.Item>
          <List.Item>
            <b>Nadzor:</b> naročniku damo na voljo informacije, potrebne za dokazovanje skladnosti, in omogočimo razumno
            najavljen pregled.
          </List.Item>
        </Seznam>
      </Clen>

      <Clen st={16} naslov="Avtorske pravice">
        <T>
          Aplikacija {IME_APLIKACIJE}, njena koda in znamka so last ponudnika. Naročnik za čas pogodbe pridobi
          neizključno in neprenosljivo pravico do uporabe aplikacije za svoje poslovanje.
        </T>
      </Clen>

      <Clen st={17} naslov="Omejitev odgovornosti">
        <T>
          Ponudnik odgovarja za škodo, ki jo povzroči namenoma ali iz hude malomarnosti. V drugih primerih je skupna
          odgovornost ponudnika omejena na znesek naročnine, ki ga je naročnik plačal za zadnji mesec pred nastankom
          škode. Ponudnik ne odgovarja za posredno škodo (npr. izgubljeni dobiček) ter za škodo zaradi napačnih
          podatkov, ki jih vnese naročnik, zaradi nepreverjenih rezultatov funkcij z umetno inteligenco (10. člen) ali
          zaradi delovanja storitev tretjih oseb.
        </T>
      </Clen>

      <Clen st={18} naslov="Prenehanje pogodbe">
        <Seznam>
          <List.Item>
            Naročnik lahko pogodbo kadar koli odpove s sporočilom prek aplikacije ali preprosto s prenehanjem plačevanja
            naročnine; dostop velja do konca plačanega obdobja, nato velja 6. člen.
          </List.Item>
          <List.Item>
            Ponudnik lahko pogodbo odpove z 30-dnevnim odpovednim rokom, ob hujši kršitvi teh pogojev pa po predhodnem
            opozorilu takoj.
          </List.Item>
        </Seznam>
      </Clen>

      <Clen st={19} naslov="Spremembe pogojev">
        <T>
          Spremembe pogojev objavimo v aplikaciji in o njih obvestimo skrbnike naročnikov najmanj 30 dni pred začetkom
          veljave. Če se naročnik s spremembami ne strinja, lahko do začetka njihove veljave pogodbo odpove; z nadaljnjo
          uporabo se šteje, da spremembe sprejema. Nove funkcije ali moduli, ki jih ti pogoji že zajemajo (npr. nova
          funkcija obstoječega modula ali nova funkcija z umetno inteligenco iz skupin v 10. členu), niso sprememba
          pogojev; opišemo jih v navodilih in novostih v aplikaciji. Prejšnje različice pogojev so na voljo na zahtevo.
        </T>
      </Clen>

      <Clen st={20} naslov="Končne določbe">
        <T>
          Za pogodbo velja pravo Republike Slovenije. Spore bomo skušali rešiti sporazumno, sicer je zanje pristojno
          stvarno pristojno sodišče v Mariboru. Če bi se katera določba izkazala za neveljavno, ostanejo ostale v
          veljavi. Ti pogoji veljajo od {RAZLICICA_POGOJEV} in nadomeščajo Splošne pogoje uporabe aplikacije Hera z dne
          26. 5. 2018.
        </T>
      </Clen>
    </Stack>
  );
}
